import { memo, useMemo } from "react"
import { useNavigate, useParams } from "@tanstack/react-router"
import { AnimatePresence, motion } from "motion/react"

import type { HelpCenterArticle, HelpCenterGroup } from "@productlane/zero"
import { cn } from "@productlane/lib/src/browser"
import {
	computeProposalDiffStats,
	mergeProposalsForArticle,
} from "@productlane/lib/src/prosemirror-diff"
import { Badge, NucleoFillIcon } from "@productlane/ui"

import type { SuggestionRow } from "./use-article-suggestions"
import { NavSectionHeader } from "@/components/shared/nav-section-header"
import { captureError } from "@/lib/capture-error"
import {
	DOCS_SECTIONS,
	useDocNavStore,
	useIsDocsListExpanded,
	useIsDocsSectionOpen,
} from "@/stores/docs/docs-nav-store"
import { DocsShowAllToggle } from "./docs-show-all-toggle"
import { useHelpCenterNav } from "./help-center-context"
import { toMergeInput, useArticleSuggestions } from "./use-article-suggestions"

interface Props {
	articleGroups: readonly (HelpCenterGroup & {
		helpCenterArticles: readonly HelpCenterArticle[]
	})[]
}

// Rows shown before the View all toggle takes over.
const COLLAPSED_LIMIT = 3

export const SuggestionsSection = memo(({ articleGroups }: Props) => {
	const { visibleRows: rows } = useArticleSuggestions()
	const isExpanded = useIsDocsListExpanded(DOCS_SECTIONS.suggestions)
	const isSectionOpen = useIsDocsSectionOpen(DOCS_SECTIONS.suggestions)
	const toggleSection = useDocNavStore((s) => s.toggleSection)
	const toggleList = useDocNavStore((s) => s.toggleList)

	const articleById = useMemo(() => {
		const map = new Map<string, HelpCenterArticle>()
		for (const g of articleGroups) {
			for (const a of g.helpCenterArticles) {
				map.set(a.id, a)
			}
		}
		return map
	}, [articleGroups])

	// Rows alone decide visibility. Gating on Zero's `complete` result type hid
	// the section indefinitely whenever one of the two proposal queries stayed
	// short of `complete`, even with rows in hand (the Docs badge and the nav
	// tree render from the same rows without waiting for it).
	if (rows.length === 0) return null

	const overflowing = rows.length > COLLAPSED_LIMIT
	const visibleRows = overflowing ? rows.slice(0, COLLAPSED_LIMIT) : rows
	const hiddenRows = overflowing ? rows.slice(COLLAPSED_LIMIT) : []

	const renderRow = (row: SuggestionRow) => {
		const article = row.articleId ? articleById.get(row.articleId) : undefined
		return (
			<SuggestionRowView
				key={row.key}
				row={row}
				articleHtml={
					typeof article?.content === "string" ? article.content : ""
				}
				articleIcon={article?.icon ?? null}
			/>
		)
	}

	return (
		<div className="group/docs-section border-b border-gray-200 px-2.5 pb-2 pt-5 dark:border-gray-700/80">
			<NavSectionHeader
				label="Suggestions"
				isOpen={isSectionOpen}
				onToggle={() => toggleSection(DOCS_SECTIONS.suggestions)}
				className="pb-1"
			/>
			<div
				inert={!isSectionOpen}
				className={cn(
					"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows,opacity] duration-200",
					isSectionOpen
						? "grid-rows-[1fr] opacity-100"
						: "grid-rows-[0fr] opacity-40",
				)}
			>
				<div className="min-h-0 overflow-hidden">
					<ul className="list-none space-y-0.5">
						{visibleRows.map(renderRow)}
					</ul>
					{/* Hidden overflow rows expand/collapse with the same height/opacity
				    animation the article-group toggles use (help-center-nav-group). */}
					<AnimatePresence initial={false}>
						{overflowing && isExpanded && (
							<motion.ul
								className="list-none space-y-0.5"
								initial={{ height: 0, opacity: 0.4 }}
								animate={{ height: "auto", opacity: 1 }}
								exit={{ height: 0, opacity: 0.4 }}
								style={{ overflow: "hidden" }}
								transition={{ duration: 0.2 }}
							>
								{hiddenRows.map(renderRow)}
							</motion.ul>
						)}
					</AnimatePresence>
				</div>
			</div>
			{isSectionOpen && overflowing && (
				<DocsShowAllToggle
					isExpanded={isExpanded}
					onToggle={() => toggleList(DOCS_SECTIONS.suggestions)}
				/>
			)}
		</div>
	)
})
SuggestionsSection.displayName = "SuggestionsSection"

function SuggestionRowView({
	row,
	articleHtml,
	articleIcon,
}: {
	row: SuggestionRow
	articleHtml: string
	articleIcon: string | null
}) {
	const navigate = useNavigate()
	const params = useParams({ strict: false })
	const { onItemSelected } = useHelpCenterNav()
	const isSelected =
		(row.articleId && params.articleId === row.articleId) ||
		(row.kind === "CREATE" && params.proposalId === row.proposals[0]?.id)

	const stats = useMemo(() => {
		// EDIT/DELETE row diffs against the article's current HTML; CREATE row
		// diffs against an empty base. Both go through the same merge helper.
		const baseHtml = row.articleId ? articleHtml : ""
		const inputs = row.articleId
			? row.proposals.map(toMergeInput)
			: row.proposals[0]
				? [toMergeInput(row.proposals[0])]
				: []
		if (inputs.length === 0) return { added: 0, removed: 0 }
		// The badge is decorative, so an unmergeable proposal drops its counts
		// rather than throwing through the nav and taking the Docs screen down.
		try {
			const merged = mergeProposalsForArticle(baseHtml, inputs)
			return computeProposalDiffStats(baseHtml, merged.resultHtml)
		} catch (error) {
			captureError(error, {
				domain: "general",
				action: "compute suggestion diff stats",
				extra: { articleId: row.articleId, rowKind: row.kind },
			})
			return { added: 0, removed: 0 }
		}
	}, [articleHtml, row.proposals, row.articleId, row.kind])

	const onClick = () => {
		if (row.articleId) {
			void navigate({
				to: "/edit-docs/review/$articleId",
				params: { articleId: row.articleId },
			})
		} else {
			const first = row.proposals[0]
			if (first)
				void navigate({
					to: "/edit-docs/review-new/$proposalId",
					params: { proposalId: first.id },
				})
		}
		onItemSelected()
	}

	// Use the article's own icon (matches the article header in
	// `help-center-article.tsx`). CREATE rows have no article yet, so fall
	// back to the same default the CREATE review uses.
	const iconName = articleIcon ?? (row.kind === "CREATE" ? "Page2" : null)

	return (
		<li>
			<button
				type="button"
				onClick={onClick}
				data-suggestion-kind={row.kind}
				className={cn(
					"group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
					isSelected
						? "bg-gray-100 text-primary dark:bg-gray-800"
						: "text-secondary hover:bg-gray-100 hover:text-primary dark:hover:bg-gray-800",
				)}
			>
				<NucleoFillIcon
					icon={iconName}
					className={cn(
						"h-3.5 w-3.5 flex-none",
						row.kind === "CREATE" ? "text-green-500" : "opacity-90",
					)}
				/>
				<span className="flex-1 translate-y-px truncate">{row.title}</span>
				{row.isOrphanDraft ? (
					<Badge variant="default" size="xs">
						Draft
					</Badge>
				) : null}
				<DiffStatBadge added={stats.added} removed={stats.removed} />
			</button>
		</li>
	)
}

function DiffStatBadge({ added, removed }: { added: number; removed: number }) {
	if (added === 0 && removed === 0) return null
	return (
		<span className="flex items-center gap-1 font-mono text-[10px] tabular-nums">
			{added > 0 && (
				<span className="text-[#15803d] dark:text-[#4ade80]">+{added}</span>
			)}
			{removed > 0 && (
				<span className="text-[#991b1b] dark:text-[#f87171]">-{removed}</span>
			)}
		</span>
	)
}
